import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { userHome } from "@/lib/guards";
import { friendlyError } from "@/lib/errors";
import { Loader2, AlertTriangle, RotateCcw } from "lucide-react";

// Magic-link landing (§9.1). Reads ?token=, exchanges it for a session and
// routes the user to their gated home. On failure we split two cases: a 4xx
// means the link itself is expired / used / invalid ("error" → ask a new one),
// while a network drop or 5xx means the token might be fine ("offline" → let the
// user retry the exchange without discarding a valid link).
export default function AuthMagic() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { loginWithMagic } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | error | offline
  const [offlineMsg, setOfflineMsg] = useState("");
  const ranRef = useRef(false);

  const exchange = useCallback(async () => {
    const token = params.get("token");
    if (!token) { setStatus("error"); return; }
    setStatus("loading");
    try {
      const user = await loginWithMagic(token);
      nav(userHome(user), { replace: true });
    } catch (err) {
      const s = err?.response?.status;
      if (s && s >= 400 && s < 500) {
        setStatus("error");
      } else {
        setOfflineMsg(friendlyError(err));
        setStatus("offline");
      }
    }
  }, [params, loginWithMagic, nav]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    exchange();
  }, [exchange]);

  return (
    <div
      className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12 relative overflow-hidden"
      data-testid="auth-magic-page"
    >
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 bloom-top pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-10">
          <Link to="/"><BrandLogo size="lg" /></Link>
        </div>
        <div className="glass-card rounded-3xl p-10 text-center">
          {status === "loading" ? (
            <div data-testid="auth-magic-loading">
              <Loader2 className="w-8 h-8 text-brand-rose animate-spin mx-auto mb-5" />
              <h1 className="font-display text-2xl text-white mb-1">Entrando…</h1>
              <p className="text-[14px] text-brand-ink2">Validando seu link de acesso.</p>
            </div>
          ) : status === "offline" ? (
            <div data-testid="auth-magic-offline">
              <div className="w-14 h-14 rounded-full bg-brand-rose/12 border border-brand-rose/40 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-6 h-6 text-brand-rose" />
              </div>
              <h1 className="font-display text-2xl text-white mb-2">Não foi possível conectar</h1>
              <p className="text-[14px] text-brand-ink2 mb-7">
                {offlineMsg || "Sem conexão com o servidor. Seu link continua válido — tente novamente."}
              </p>
              <button
                type="button"
                data-testid="auth-magic-retry-connection"
                onClick={exchange}
                className="pill-pink w-full py-3.5 text-[14px] tracking-wide inline-flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Tentar novamente
              </button>
            </div>
          ) : (
            <div data-testid="auth-magic-error">
              <div className="w-14 h-14 rounded-full bg-brand-rose/12 border border-brand-rose/40 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-6 h-6 text-brand-rose" />
              </div>
              <h1 className="font-display text-2xl text-white mb-2">Link expirado ou inválido</h1>
              <p className="text-[14px] text-brand-ink2 mb-7">
                Este link de acesso não é mais válido. Peça um novo — leva um instante.
              </p>
              <button
                type="button"
                data-testid="auth-magic-retry"
                onClick={() => nav("/login", { replace: true })}
                className="pill-pink w-full py-3.5 text-[14px] tracking-wide"
              >
                Pedir um novo link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
